import React from 'react';

import { Item2BreadcrumbsData, Category } from '../schemas/rql';
import { uniqWith } from 'lodash';
import URLHelpers from './shared/url_helpers';
import Breadcrumbs from '@reverbdotcom/commons/src/components/breadcrumbs';
import gql from 'graphql-tag';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { MParticleEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';

import { findProductFamilySpec } from '@reverbdotcom/commons/src/product_family_helpers';

interface IProps {
  listing: Item2BreadcrumbsData.Fragment;
}

export const Item2BreadcrumbsFragment = gql`
  fragment Item2BreadcrumbsData on Listing {
    _id
    id
    make
    brandSlug
    categories {
      _id
      id
      slug
      name
      rootSlug
      webLink {
        href
      }
      cmsLink {
        href
      }
    }
    csp {
      _id
      id
      specs {
        productSpecs {
          type
          name
          value
          path
        }
      }
    }
  }
`;

export function componentClickEvent(displayStyle, path) {
  return {
    eventName: MParticleEventName.ClickedComponent,
    componentName: 'Item2Breadcrumbs',
    displayStyle,
    entityType: 'url',
    entityId: path,
  };
}

function CategoryBreadcrumb(props: { cat: Item2BreadcrumbsData.Categories, idx: number }) {
  let { href } = props.cat.webLink;
  if (props.cat.cmsLink?.href) {
    href = props.cat.cmsLink.href;
  }

  return (
    <>
      <CoreLink
        to={href}
        clickEvent={componentClickEvent('categoryBreadcrumb', href)}
        itemProp="item"
      >
        <span itemProp="name">
          {props.cat.name}
        </span>
      </CoreLink>
      <meta itemProp="position" content={`${props.idx + 1}`} />
    </>
  );
}

export function Categories(listing) {
  return uniqWith(
    listing.categories,
    (a: Category, b: Category) => (a.rootSlug === b.rootSlug),
  );
}

export function BrandBreadcrumbParams(listing) {
  const categories = Categories(listing);
  const childCat = categories.find((c) => !!c.rootSlug.length);

  if (!childCat) {
    return { product_type: categories[0]?.slug };
  }
  return {
    product_type: childCat.rootSlug,
    category: childCat.slug,
  };
}

function BrandBreadcrumb({ listing }: IProps) {
  const params = { ...BrandBreadcrumbParams(listing) };

  return (
    <>
      <CoreLink
        to={ URLHelpers.brandPath(listing.brandSlug, params) }
        clickEvent={ componentClickEvent('brandBreadcrumb', URLHelpers.brandPath(listing.brandSlug, params)) }
        itemProp="item"
      >
        <span itemProp="name">
          {listing.make}
        </span>
      </CoreLink>
      <meta itemProp="position" content={`${Categories(listing).length + 1}`} />
    </>
  );
}

function ProductFamilyBreadcrumb({ listing }: IProps) {
  const productFamily = findProductFamilySpec(listing.csp?.specs?.productSpecs);

  return (
    <>
      <CoreLink
        to={productFamily.path}
        data-product-family-link
        clickEvent={componentClickEvent('productFamilyBreadcrumb', productFamily.path)}
        target="_top"
        itemProp="item"
      >
        <span itemProp="name">
          {productFamily.value}
        </span>
      </CoreLink>
      <meta itemProp="position" content={`${Categories(listing).length + 2}`} />
    </>
  );
}

export function Item2Breadcrumbs({ listing }: IProps) {
  const mparticleEvent: MParticleEvent = {
    componentName: 'ProductFamilyCTA',
    eventName: MParticleEventName.ComponentView,
  };

  // only show brand breadcrumb if listing has a brand slug
  const renderBrandBreadcrumb = listing?.brandSlug;

  // track a component view if a product family exists
  const productFamily = findProductFamilySpec(listing?.csp?.specs?.productSpecs);
  const renderProductFamilyBreadcrumb = !!productFamily;

  useViewTracking(mparticleEvent, !!productFamily);

  if (!listing) return null;

  return (
    <div className="item2-breadcrumbs">
      <Breadcrumbs>
        {Categories(listing).map((cat, idx) => (
          <CategoryBreadcrumb cat={cat} key={cat.id} idx={idx} />
        ))}
        { renderBrandBreadcrumb && <BrandBreadcrumb listing={ listing } /> }
        {renderProductFamilyBreadcrumb && <ProductFamilyBreadcrumb listing={listing} />}
      </Breadcrumbs>
    </div>
  );
}
